import React, { useEffect, useState } from "react";
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";

type AdminGateResult =
  | { loading: true; ok: false }
  | { loading: false; ok: true }
  | { loading: false; ok: false; message: string };

const ALLOWED_INTERNAL_ROLES = new Set([
  "saas_owner",
  "saas_senior",
  "saas_support",
]);

function AdminShellLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-accent border-t-transparent" />

        <p className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">
          Loading admin console...
        </p>
      </div>
    </div>
  );
}

function AdminAccessDenied({
  message,
}: {
  message: string;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-6 text-white">
      <div className="w-full max-w-xl rounded-3xl border border-slate-800 bg-slate-900 p-8 shadow-sm">
        <p className="text-[10px] font-black uppercase tracking-[0.35em] text-brand-accent">
          Internal Admin
        </p>

        <h2 className="mt-3 text-2xl font-black text-white">
          Admin access unavailable
        </h2>

        <p className="mt-3 text-sm leading-6 text-slate-300">
          {message}
        </p>

        <div className="mt-6">
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-xl bg-brand-accent px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-white transition hover:opacity-90"
          >
            Return to login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function AdminShellLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    initializing,
    isAuthenticated,
    displayName,
    signOut,
  } = useAuth();

  const [adminGate, setAdminGate] =
    useState<AdminGateResult>({
      loading: true,
      ok: false,
    });

  useEffect(() => {
    let cancelled = false;

    async function verifyAdminAccess() {
      if (initializing) {
        return;
      }

      if (!isAuthenticated) {
        if (!cancelled) {
          setAdminGate({
            loading: false,
            ok: false,
            message: "You must be signed in.",
          });
        }

        return;
      }

      const { data, error } =
        await supabase.auth.getSession();

      if (cancelled) {
        return;
      }

      if (error) {
        setAdminGate({
          loading: false,
          ok: false,
          message:
            error.message ||
            "Unable to verify admin session.",
        });

        return;
      }

      const session = data.session;

      const internalRole =
        typeof session?.user?.app_metadata
          ?.internal_role === "string"
          ? session.user.app_metadata.internal_role.trim()
          : "";

      if (
        !ALLOWED_INTERNAL_ROLES.has(internalRole)
      ) {
        setAdminGate({
          loading: false,
          ok: false,
          message:
            "Internal compliance admin authority required.",
        });

        return;
      }

      setAdminGate({
        loading: false,
        ok: true,
      });
    }

    void verifyAdminAccess();

    return () => {
      cancelled = true;
    };
  }, [initializing, isAuthenticated]);

  async function handleLogout() {
    await signOut();

    navigate("/login", {
      replace: true,
    });
  }

  if (initializing || adminGate.loading) {
    return <AdminShellLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!adminGate.ok) {
    return (
      <AdminAccessDenied
        message={adminGate.message}
      />
    );
  }

  const pathname = location.pathname;

  const isComplianceVersionsRoute =
    pathname === "/admin/compliance";

  const isComplianceWorkspaceRoute =
    pathname.startsWith("/admin/compliance/") &&
    pathname !== "/admin/compliance";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="sticky top-0 z-50 border-b border-slate-800 bg-slate-900/80 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-6 px-6 py-4">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.35em] text-brand-accent">
              Internal Admin
            </p>

            <h1 className="mt-1 text-lg font-black tracking-wide text-white">
              Compliance Console
            </h1>

            <p className="mt-1 text-sm text-slate-400">
              Internal statutory compliance authoring and deployment workspace.
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <div className="text-right">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">
                Authenticated as
              </p>

              <p className="mt-1 text-sm font-medium text-slate-200">
                {displayName || "Internal user"}
              </p>
            </div>

            <button
              type="button"
              onClick={() => void handleLogout()}
              className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-950 px-4 py-2 text-[10px] font-black uppercase tracking-[0.25em] text-slate-200 transition hover:bg-slate-800"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-7xl gap-6 px-6 py-6">
        <aside className="hidden w-72 shrink-0 lg:block">
          <div className="rounded-3xl border border-slate-800 bg-slate-900 p-4 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-500">
              Admin Navigation
            </p>

            <nav className="mt-4 space-y-2">
              <Link
                to="/admin/compliance"
                className={`block rounded-2xl px-4 py-3 text-sm font-semibold transition ${
                  isComplianceVersionsRoute
                    ? "bg-brand-accent text-white"
                    : "bg-slate-950 text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
              >
                Compliance Versions
              </Link>

              <div
                className={`rounded-2xl px-4 py-3 text-sm font-semibold ${
                  isComplianceWorkspaceRoute
                    ? "bg-slate-800 text-white"
                    : "bg-slate-950 text-slate-500"
                }`}
              >
                Compliance Workspace
              </div>


            </nav>

            <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-950 p-4">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-accent">
                Environment
              </p>

              <p className="mt-2 text-sm leading-6 text-slate-400">
                This console is separate from the customer workspace and should
                only be used by authorized internal staff.
              </p>
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1">
          <div className="rounded-3xl border border-slate-800 bg-white p-6 text-slate-900 shadow-sm">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}